import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  
  // Initialize language from URL or localStorage
  const [language, setLanguage] = useState(() => {
    // First check URL on initial load
    const path = window.location.pathname;
    if (path.startsWith("/ar")) {
      localStorage.setItem("preferredLanguage", "ar");
      return "ar";
    }
    // Then check localStorage for preference
    const storedLang = localStorage.getItem("preferredLanguage");
    if (storedLang === "ar" || storedLang === "en") {
      return storedLang;
    }
    return "en";
  });

  // Store language preference in localStorage when it changes
  useEffect(() => {
    localStorage.setItem("preferredLanguage", language);
  }, [language]);

  // Only sync language with URL on initial mount or when URL explicitly changes language prefix
  // After initial load, maintain language state independently
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const path = location.pathname;
    const hasArabicPrefix = path.startsWith("/ar");
    
    // Only update language if URL explicitly indicates a language change
    // If URL has /ar but we're in English, switch to Arabic
    // If URL doesn't have /ar, don't automatically switch to English - preserve preference
    if (hasArabicPrefix && language !== "ar") {
      setLanguage("ar");
    }
    // Don't reset to English based on URL - preserve user's language preference
  }, [location.pathname]);

  const changeLanguage = (newLang) => {
    const currentPath = location.pathname;
    const currentSearch = location.search; // Preserve query parameters
    let newPath;

    if (newLang === "ar") {
      // Add /ar prefix
      if (currentPath.startsWith("/ar")) {
        newPath = currentPath; // Already has /ar
      } else {
        newPath = `/ar${currentPath === "/" ? "" : currentPath}`;
      }
    } else {
      // Remove /ar prefix
      if (currentPath.startsWith("/ar")) {
        newPath = currentPath.replace("/ar", "") || "/";
      } else {
        newPath = currentPath; // Already no /ar
      }
    }

    setLanguage(newLang);
    localStorage.setItem("preferredLanguage", newLang);
    // Preserve query parameters when navigating
    navigate(`${newPath}${currentSearch}`);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}
